import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "../agent-response.module.scss";
import { useAppDispatch, RootState } from "../../../store";
import { useSelector } from "react-redux";
import { setSowAutoFillerFeedback } from "../../../store/filtersSlice";
import { recordAgentAction } from "../../../store/agentSlice";

type ExtractedData = {
  [key: string]: string | number | null | undefined;
};

type SowAutoFillerAgentProps = {
  parsedContent: {
    finalAnswer?: {
      message?: string;
      question?: string;
      detected_category?: string;
      extracted_data?: ExtractedData;
      filled_document_path?: string;
      download_url?: string;
      filled_fields?: string[];
      missing_fields?: string[];
      status?: string;
    };
    message?: string;
    question?: string;
    detected_category?: string;
    extracted_data?: ExtractedData;
    filled_document_path?: string;
    download_url?: string;
    filled_fields?: string[];
    missing_fields?: string[];
    status?: string;
  };
  messageId?: string;
};

const BACKEND_KEY_TO_DISPLAY_NAME: Record<string, string> = {
  vendor_name: "Vendor Name",
  services_description: "Services",
  fees: "Fees",
  contract_duration: "Term",
  effective_date: "SOW Effective Date",
  performance_standards: "Performance Standards",
  service_credits: "Service Credits",
  liquidated_damages: "Liquidated Damages",
  bond_sum: "Bond Sum",
  deliverables: "Deliverables",
  milestones: "Milestones, Milestone Dates or other timescales for delivery",
  timescales: "Timescales",
  acceptance_criteria: "Acceptance Criteria",
  num_resources: "Number of Resources",
  skill_level: "Skill Level",
  work_location: "Work Location",
  work_schedule: "Work Schedule",
  daily_rate: "Daily Rate",
  background_objectives: "Background and Objectives",
  assumptions: "Assumptions",
  purchase_order_number: "Purchase Order Number",
  invoice_email: "Invoices",
  supplier_project_representative: "Supplier Project Representative",
  g42_project_representative: "G42 Project Representative",
  master_agreement_date: "Master Agreement Date",
  category: "Category",
  confidence: "Confidence",
  missing_fields: "Missing Fields",
};

const PREFERRED_FIELD_ORDER = [
  "vendor_name",
  "services_description",
  "effective_date",
  "background_objectives",
  "deliverables",
  "fees",
  "performance_standards",
  "service_credits",
  "liquidated_damages",
  "bond_sum",
  "milestones",
  "timescales",
  "supplier_project_representative",
  "g42_project_representative",
  "acceptance_criteria",
  "assumptions",
  "contract_duration",
  "work_location",
  "work_schedule",
  "num_resources",
  "skill_level",
  "daily_rate",
  "purchase_order_number",
  "invoice_email",
  "master_agreement_date",
];

const SKIP_FIELDS = ["category", "confidence", "missing_fields"];

const AI_IMPROVABLE_FIELDS = [
  "services_description",
  "background_objectives",
  "deliverables",
  "performance_standards",
  "acceptance_criteria",
];

// Component for accordion item - shows field with expand/collapse functionality
const AccordionRow = ({
  fieldName,
  backendKey,
  value,
  onSaveEdit,
  onAIRewrite,
  isModified = false,
  isAIImprovable = false,
}: {
  fieldName: string;
  backendKey: string;
  value: string;
  onSaveEdit: (backendKey: string, oldValue: string, newValue: string) => void;
  onAIRewrite: (
    fieldName: string,
    backendKey: string,
    currentValue: string,
  ) => void;
  isModified?: boolean;
  isAIImprovable?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  // Update editValue when value prop changes (for modified fields)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    navigator.clipboard.writeText(value);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    setIsEditing(true);
    setEditValue(value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editValue.trim() === "") {
      alert("Value cannot be empty");
      return;
    }
    if (editValue !== value) {
      onSaveEdit(backendKey, value, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(value);
    setIsEditing(false);
  };

  const handleAIRewrite = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    onAIRewrite(fieldName, backendKey, value);
  };

  return (
    <div
      style={{
        border: "1px solid #b2e5de",
        borderRadius: "8px",
        marginBottom: "16px",
        overflow: "hidden",
        backgroundColor: isModified ? "#fff4e6" : "#fff",
      }}
    >
      <Box
        sx={{
          background: "#f0fbf9",
          padding: "16px 20px",
          cursor: "pointer",
          transition: "background 0.2s",
          "&:hover": {
            background: "#f3f4f6",
          },
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: 500,
            color: "#004141",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isOpen ? (
            <ExpandLessIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          ) : (
            <ExpandMoreIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          )}
          {fieldName}
          {isModified && (
            <Chip
              label="Modified"
              size="small"
              sx={{
                height: "18px",
                fontSize: "10px",
                backgroundColor: "#ff9800",
                color: "#fff",
                fontWeight: 600,
                marginLeft: "8px",
              }}
            />
          )}
        </h3>
      </Box>
      {isOpen && (
        <div
          style={{
            padding: "20px",
            background: "#ffffff",
            position: "relative",
          }}
        >
          {!isEditing ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                }}
              >
                {isAIImprovable && (
                  <Tooltip title="AI Rewrite" arrow>
                    <IconButton
                      size="small"
                      onClick={handleAIRewrite}
                      sx={{
                        padding: "3px",
                        minWidth: "28px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f0f9ff",
                        },
                      }}
                    >
                      <AutoFixHighIcon
                        style={{
                          color: "#3b82f6",
                          fontSize: "20px",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit" arrow>
                  <IconButton
                    size="small"
                    onClick={handleEditClick}
                    sx={{
                      padding: "3px",
                      minWidth: "28px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    <EditIcon
                      style={{
                        color: "#9ca3af",
                        fontSize: "20px",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title={copySuccess ? "Copied!" : "Copy"} arrow>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                      padding: "3px",
                      minWidth: "28px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    <ContentCopyIcon
                      style={{
                        color: copySuccess ? "#00a888" : "#9ca3af",
                        fontSize: "20px",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                component="div"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  color: "#403f3f",
                  fontSize: "14px",
                  lineHeight: 1.6,
                  paddingRight: "70px",
                }}
              >
                {value}
              </Typography>
            </>
          ) : (
            <Box sx={{ paddingRight: 0 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fafafa",
                    "&:hover fieldset": {
                      borderColor: "#00a888",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#00a888",
                    },
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  marginTop: 2,
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancel}
                  sx={{
                    textTransform: "none",
                    borderColor: "#d1d5db",
                    color: "#6b7280",
                    "&:hover": {
                      borderColor: "#9ca3af",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleSave}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#00a888",
                    "&:hover": {
                      backgroundColor: "#008f73",
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </div>
      )}
    </div>
  );
};

export default function SowAutoFillerAgent({
  parsedContent,
  messageId = "",
}: SowAutoFillerAgentProps): JSX.Element {
  // DEBUG: Log raw parsedContent
  console.log("[SowAutoFillerAgent] Raw parsedContent:", parsedContent);
  console.log("[SowAutoFillerAgent] messageId:", messageId);

  const dispatch = useAppDispatch();
  const currentAgent = useSelector((state: RootState) => state.agents.currentAgent);
  const numericAgentId = currentAgent?.id || 11;
  const { sowAutoFillerFeedback } = useSelector(
    (state: RootState) => state.filters,
  );
  const currentFeedback = sowAutoFillerFeedback[messageId] || {
    sow_modify_array: [],
  };
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<"direct" | "ai">("direct");
  const [currentEdit, setCurrentEdit] = useState({
    fieldName: "",
    backendKey: "",
    oldValue: "",
    newValue: "",
  });

  const data = parsedContent?.finalAnswer || parsedContent;

  // DEBUG: Log extracted data object
  console.log("[SowAutoFillerAgent] Extracted data object:", data);

  const message = data?.message || "";
  const question = data?.question || "";
  const extractedData = data?.extracted_data || {};
  const downloadUrl = data?.download_url || "";
  const filledFields = data?.filled_fields || [];
  const missingFields = data?.missing_fields || [];
  const status = data?.status || "";

  // DEBUG: Log all extracted fields
  console.log("[SowAutoFillerAgent] Fields breakdown:", {
    message: message ? message.substring(0, 100) + "..." : "",
    question,
    extractedData,
    downloadUrl,
    filledFields,
    missingFields,
    status,
  });

  const filename = useMemo(() => {
    if (!downloadUrl) return "SOW_Document.docx";
    try {
      const url = new URL(downloadUrl);
      const pathname = url.pathname;
      const parts = pathname.split("/");
      return parts[parts.length - 1] || "SOW_Document.docx";
    } catch {
      return "SOW_Document.docx";
    }
  }, [downloadUrl]);

  const messageHeader = useMemo(() => {
    if (!message) return "";

    const tableStart = message.indexOf("| Field |");
    if (tableStart === -1) return message;

    let header = message.substring(0, tableStart).trim();

    header = header.replace(/\*\*Changes Applied!\*\*[\s\S]*?---/g, "");
    header = header.replace(/###?\s*What Changed:[\s\S]*?(?=##|$)/g, "");
    header = header.replace(/---+/g, "");
    header = header.replace(/\n{3,}/g, "\n\n");

    return header.trim();
  }, [message]);

  const getFieldValue = (backendKey: string): string => {
    // Find the most recent modification for this field (search from end)
    const modifications = currentFeedback.sow_modify_array.filter(
      (mod) => mod.field === backendKey,
    );

    if (modifications.length > 0) {
      const latestMod = modifications[modifications.length - 1];
      // Return new_value if it's a direct edit
      if ("new_value" in latestMod) {
        return latestMod.new_value;
      }
      // For AI rewrites, show original value until backend responds
      // (AI modifications will be processed by backend)
    }

    const value = extractedData[backendKey];
    return value !== null && value !== undefined ? String(value) : "";
  };

  const isFieldModified = (backendKey: string): boolean => {
    return currentFeedback.sow_modify_array.some(
      (mod) => mod.field === backendKey,
    );
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isAIImprovableField = (backendKey: string): boolean => {
    return AI_IMPROVABLE_FIELDS.includes(backendKey);
  };

  const tableFields = useMemo(() => {
    if (!extractedData || Object.keys(extractedData).length === 0) return [];

    const orderedFields: Array<{ key: string; display: string }> = [];
    const unknownFields: Array<{ key: string; display: string }> = [];

    Object.keys(extractedData).forEach((key) => {
      if (SKIP_FIELDS.includes(key)) return;

      const value = extractedData[key];
      if (value === null || value === undefined || value === "") return;

      const displayName =
        BACKEND_KEY_TO_DISPLAY_NAME[key] || formatFieldName(key);
      const fieldObj = { key, display: displayName };

      if (PREFERRED_FIELD_ORDER.includes(key)) {
        orderedFields.push(fieldObj);
      } else {
        unknownFields.push(fieldObj);
      }
    });

    orderedFields.sort((a, b) => {
      const indexA = PREFERRED_FIELD_ORDER.indexOf(a.key);
      const indexB = PREFERRED_FIELD_ORDER.indexOf(b.key);
      return indexA - indexB;
    });

    // DEBUG: Log tableFields computed
    console.log("[SowAutoFillerAgent] tableFields computed:", {
      orderedFields,
      unknownFields,
    });

    return [...orderedFields, ...unknownFields];
  }, [extractedData]);

  // Handle inline edit save
  const handleSaveEdit = (
    backendKey: string,
    oldValue: string,
    newValue: string,
  ) => {
    // Remove any existing modifications for this field to avoid duplicates
    const filteredArray = currentFeedback.sow_modify_array.filter(
      (mod) => mod.field !== backendKey,
    );

    const modificationObject = {
      field: backendKey,
      old_value: oldValue,
      new_value: newValue,
    };

    dispatch(
      setSowAutoFillerFeedback({
        messageId,
        feedback: {
          sow_modify_array: [...filteredArray, modificationObject],
        },
      }),
    );
  };

  // Handle AI Rewrite button click (opens modal)
  const handleAIRewrite = (
    displayName: string,
    backendKey: string,
    currentValue: string,
  ) => {
    // Check if there's an existing AI rewrite instruction for this field
    const existingMod = currentFeedback.sow_modify_array
      .filter((mod) => mod.field === backendKey)
      .reverse()
      .find((mod) => "agent_instructions" in mod);

    setCurrentEdit({
      fieldName: displayName,
      backendKey,
      oldValue: currentValue,
      newValue: existingMod?.agent_instructions || "",
    });
    setEditMode("ai");
    setIsEditModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditMode("direct");
    setCurrentEdit({
      fieldName: "",
      backendKey: "",
      oldValue: "",
      newValue: "",
    });
  };

  const handleSaveAIRewrite = () => {
    if (currentEdit.newValue.trim() === "") {
      alert("Instructions cannot be empty");
      return;
    }

    // Remove any existing modifications for this field to avoid duplicates
    const filteredArray = currentFeedback.sow_modify_array.filter(
      (mod) => mod.field !== currentEdit.backendKey,
    );

    const modificationObject = {
      field: currentEdit.backendKey,
      old_value: currentEdit.oldValue,
      agent_instructions: currentEdit.newValue,
    };

    dispatch(
      setSowAutoFillerFeedback({
        messageId,
        feedback: {
          sow_modify_array: [...filteredArray, modificationObject],
        },
      }),
    );

    handleCloseModal();
  };

  const handleDownload = () => {
    if (!downloadUrl) {
      alert("Download URL is not available. Please try again.");
      return;
    }

    // Record download action - fire and forget (non-blocking)
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "download",
        content: JSON.stringify({
          type: "sow_filled_document",
          fileName: filename,
          downloadUrl,
        }),
      })
    );

    try {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert("Failed to download the document. Please try again.");
    }
  };

  // DEBUG: Log what will be rendered
  console.log("[SowAutoFillerAgent] Render state:", {
    hasMessage: !!message,
    hasQuestion: !!question,
    hasDownloadUrl: !!downloadUrl,
    tableFieldsCount: tableFields.length,
    filledFieldsCount: filledFields.length,
    missingFieldsCount: missingFields.length,
    status,
    isShowingChecklist: status === "showing_checklist",
  });

  return (
    <div className={styles.pressReleaseContent}>
      {/* For showing_checklist status, display the full message */}
      {status === "showing_checklist" && message && (
        <Box sx={{ marginBottom: 3 }}>
          <MarkdownRenderer content={message} />
        </Box>
      )}

      {/* Main Message Header (before table) - only show if not showing_checklist */}
      {status !== "showing_checklist" && messageHeader && (
        <Box sx={{ marginBottom: 3 }}>
          <MarkdownRenderer content={messageHeader} />
        </Box>
      )}

      {/* Extracted Information Accordion - only show if not showing_checklist */}
      {status !== "showing_checklist" && tableFields.length > 0 && (
        <Box
          sx={{
            marginBottom: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {tableFields.map((field, index) => {
            const value = getFieldValue(field.key);
            return value ? (
              <AccordionRow
                key={index}
                fieldName={field.display}
                backendKey={field.key}
                value={value}
                onSaveEdit={handleSaveEdit}
                onAIRewrite={handleAIRewrite}
                isModified={isFieldModified(field.key)}
                isAIImprovable={isAIImprovableField(field.key)}
              />
            ) : null;
          })}
        </Box>
      )}

      {/* Document Download Section - only show if not showing_checklist */}
      {status !== "showing_checklist" && downloadUrl && (
        <Paper
          elevation={0}
          sx={{
            padding: 2.5,
            marginBottom: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #B2E5DE",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#004141" }}
                >
                  Ready to download
                </Typography>
                <Typography variant="body2" sx={{ color: "#403f3f" }}>
                  {filename}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!downloadUrl}
              sx={{
                textTransform: "none",
                paddingX: 3,
                backgroundColor: "#5fcfac",
                "&:hover": { backgroundColor: "#4fb89a" },
              }}
            >
              Download DOCX
            </Button>
          </Box>
        </Paper>
      )}

      {/* SOW sections found and filled - only show if not showing_checklist */}
      {status !== "showing_checklist" && filledFields.length > 0 && (
        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              backgroundColor: "#f0fbf9",
              border: "1px solid #B2E5DE",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <CheckCircleIcon sx={{ color: "#00a888", fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#004141" }}
              >
                SOW sections found and filled ({filledFields.length})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {filledFields.map((field: string, index: number) => (
                <Chip
                  key={index}
                  label={field}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: "11px",
                    border: "1px solid #B2E5DE",
                    color: "#403f3f",
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Missing Fields - only show if not showing_checklist */}
      {status !== "showing_checklist" && missingFields.length > 0 && (
        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              backgroundColor: "#fef9f5",
              border: "1px solid #fed7aa",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <WarningAmberIcon sx={{ color: "#905d13", fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#905d13" }}
              >
                SOW sections not found in input documents (
                {missingFields.length})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {missingFields.map((field: string, index: number) => (
                <Chip
                  key={index}
                  label={formatFieldName(field)}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: "11px",
                    border: "1px solid #fed7aa",
                    color: "#905d13",
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Question Section */}
      {question && (
        <div
          style={{
            marginTop: "20px",
            paddingTop: "16px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              color: "#004141",
              fontWeight: "600",
              margin: 0,
              whiteSpace: "pre-line",
            }}
          >
            {question}
          </p>
        </div>
      )}

      {/* AI Rewrite Modal Dialog */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f0f9ff",
            color: "#004141",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AutoFixHighIcon sx={{ color: "#3b82f6" }} />
          AI Rewrite: {currentEdit.fieldName}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "20px !important" }}>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            label="AI Instructions"
            placeholder="Provide instructions for AI to improve this field..."
            value={currentEdit.newValue}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, newValue: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#3b82f6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3b82f6",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#3b82f6",
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              marginTop: 1,
              color: "#6b7280",
              fontStyle: "italic",
            }}
          >
            Example: "Make it more formal and add technical details" or "Expand
            on security requirements"
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              textTransform: "none",
              color: "#403f3f",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAIRewrite}
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Regenerate with AI
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
